import { type TimeRange } from '~/swr-hooks/clips/transcript/types';

export type TimeIntervalResponse = {
  previousEndTimeMs: number;
  nextStartTimeMs: number;
};

export type DataForTimeInterval<T, V = undefined> = {
  startTimeMs: number;
  endTimeMs: number;
  data: T[];
  metadata?: V;
};

export type FetchedDataForTimeInterval<T, V = undefined> = { data: T[]; metadata?: V } & TimeIntervalResponse;

export type GetDataForTimeIntervalParams<T, V = undefined> = {
  initialData: DataForTimeInterval<T, V>[];
  startTimeMs: number;
  endTimeMs: number;
  fetchData: (params: TimeRange) => Promise<FetchedDataForTimeInterval<T, V>>;
};

const isInRange = (numberToCheck: number, range: [number, number]) =>
  range[0] <= numberToCheck && numberToCheck <= range[1];

export const getDataForTimeInterval = async <T, V = undefined>({
  startTimeMs,
  endTimeMs,
  initialData,
  fetchData,
}: GetDataForTimeIntervalParams<T, V>): Promise<DataForTimeInterval<T, V>[]> => {
  if (initialData.length === 0) {
    const newData = await fetchData({ startTimeMs, endTimeMs });
    return [
      {
        startTimeMs: newData.previousEndTimeMs,
        endTimeMs: newData.nextStartTimeMs,
        data: newData.data,
        metadata: newData.metadata,
      },
    ];
  }

  for (let i = 0; i < initialData.length; i++) {
    const data = initialData[i];

    // range already exists, do not fetch - e.g. existing [1,6], requested [2,3]
    if (data.startTimeMs <= startTimeMs && endTimeMs <= data.endTimeMs) {
      return Promise.resolve(initialData);
    }

    if (isInRange(startTimeMs, [data.startTimeMs, data.endTimeMs]) && data.endTimeMs <= endTimeMs) {
      // range overlaps with existing on the left - e.g. existing [1,6], requested [4,8]
      const nextIntervalData = i < initialData.length ? initialData[i + 1] : null;
      const endTimeToFetch =
        nextIntervalData && isInRange(endTimeMs, [nextIntervalData.startTimeMs, nextIntervalData.endTimeMs])
          ? nextIntervalData.startTimeMs
          : endTimeMs;

      const newData = await fetchData({ startTimeMs: data.endTimeMs, endTimeMs: endTimeToFetch });

      if (nextIntervalData) {
        // check if new data end time overlaps with the next range
        // and merge previous, current and next data if so
        if (newData.nextStartTimeMs >= nextIntervalData.startTimeMs) {
          const copyData = [...initialData];
          copyData.splice(i, 2, {
            ...copyData[i],
            metadata: newData.metadata,
            data: [...copyData[i].data, ...newData.data, ...nextIntervalData.data],
            endTimeMs: nextIntervalData.endTimeMs,
          });
          return copyData;
        }
      }

      const copyData = [...initialData];

      copyData.splice(i, 1, {
        ...copyData[i],
        metadata: newData.metadata,
        data: [...copyData[i].data, ...newData.data],
        endTimeMs: newData.nextStartTimeMs,
      });
      return copyData;
    } else if (startTimeMs <= data.startTimeMs && isInRange(endTimeMs, [data.startTimeMs, data.endTimeMs])) {
      // range overlaps with existing on the right - e.g. existing [4,8], requested [1,6]
      const previousIntervalData = i > 0 ? initialData[i - 1] : null;
      const startTimeToFetch =
        previousIntervalData &&
        isInRange(startTimeMs, [previousIntervalData.startTimeMs, previousIntervalData.endTimeMs])
          ? previousIntervalData.endTimeMs
          : startTimeMs;

      const newData = await fetchData({ startTimeMs: startTimeToFetch, endTimeMs: data.startTimeMs });
      if (previousIntervalData) {
        // check if new data start time overlaps with the previous range
        // and merge previous, current and next data if so
        if (newData.previousEndTimeMs <= previousIntervalData.endTimeMs) {
          const copyData = [...initialData];
          copyData.splice(i - 1, 2, {
            ...copyData[i],
            metadata: newData.metadata,
            data: [...previousIntervalData.data, ...newData.data, ...copyData[i].data],
            startTimeMs: previousIntervalData.startTimeMs,
          });
          return copyData;
        }
      }

      const copyData = [...initialData];
      copyData.splice(i, 1, {
        ...copyData[i],
        metadata: newData.metadata,
        data: [...newData.data, ...copyData[i].data],
        startTimeMs: newData.previousEndTimeMs,
      });
      return copyData;
    }

    const isBeforeCurrentDataRange = data.startTimeMs > endTimeMs;

    if (isBeforeCurrentDataRange) {
      // range not found, e.g. existing [1,6],[11,15], requested [8,10]
      const newData = await fetchData({ startTimeMs, endTimeMs });

      const previousIntervalData = i > 0 ? initialData[i - 1] : null;
      const nextIntervalData = i < initialData.length ? initialData[i] : null;

      let dataToInsert = [...newData.data];
      let rangeStartTime = newData.previousEndTimeMs;
      let rangeEndTime = newData.nextStartTimeMs;
      let overlapsOnLeft = false;
      if (previousIntervalData && newData.previousEndTimeMs <= previousIntervalData.endTimeMs) {
        dataToInsert = [...previousIntervalData.data, ...dataToInsert];
        rangeStartTime = previousIntervalData.startTimeMs;
        overlapsOnLeft = true;
      }

      let overlapsOnRight = false;
      if (nextIntervalData && newData.nextStartTimeMs >= nextIntervalData.startTimeMs) {
        dataToInsert = [...dataToInsert, ...nextIntervalData.data];
        rangeEndTime = nextIntervalData.endTimeMs;
        overlapsOnRight = true;
      }

      const newTimeRangeData: DataForTimeInterval<T, V> = {
        metadata: newData.metadata,
        startTimeMs: rangeStartTime,
        endTimeMs: rangeEndTime,
        data: dataToInsert,
      };

      const copyData = [...initialData];

      if (overlapsOnLeft && overlapsOnRight) {
        copyData.splice(i - 1, 2, newTimeRangeData);
      } else if (overlapsOnLeft) {
        copyData.splice(i - 1, 1, newTimeRangeData);
      } else if (overlapsOnRight) {
        copyData.splice(i, 1, newTimeRangeData);
      } else {
        copyData.splice(i, 0, newTimeRangeData);
      }

      return copyData;
    }
  }

  // if no data was returned - requested range is after the last range, e.g. existing [1,6], requested [8,10]
  const newData = await fetchData({ startTimeMs, endTimeMs });
  const previousIntervalData = initialData.length > 1 ? initialData[initialData.length - 1] : null;

  let dataToInsert = [...newData.data];
  let rangeStartTime = newData.previousEndTimeMs;
  const rangeEndTime = newData.nextStartTimeMs;
  let overlapsOnLeft = false;
  if (previousIntervalData && newData.previousEndTimeMs <= previousIntervalData.endTimeMs) {
    dataToInsert = [...previousIntervalData.data, ...dataToInsert];
    rangeStartTime = previousIntervalData.startTimeMs;
    overlapsOnLeft = true;
  }

  const copyData = [...initialData];

  const newTimeRangeData: DataForTimeInterval<T, V> = {
    metadata: newData.metadata,
    startTimeMs: rangeStartTime,
    endTimeMs: rangeEndTime,
    data: dataToInsert,
  };

  if (overlapsOnLeft) {
    copyData.splice(initialData.length - 1, 2, newTimeRangeData);
  } else {
    copyData.push(newTimeRangeData);
  }

  return copyData;
};
